import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { UtensilsCrossed, Store, ShieldCheck, BadgeCheck, Truck, Handshake } from 'lucide-react';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { Card } from '@atoms/Card';
import { authService } from '@services/authService';
import { useAuthStore } from '@store/authStore';
import { getErrorMessage } from '@services/api';
import { unlockMessageSound } from '@utils/messageSound';
import { ensureNotificationPermission } from '@utils/desktopNotification';
import type { UserRole } from '@/types';

const ROLES: { id: UserRole; label: string; icon: LucideIcon; desc: string }[] = [
  { id: 'restaurant', label: 'Restoran', icon: UtensilsCrossed, desc: 'Toptancılardan sipariş verin' },
  { id: 'wholesaler', label: 'Toptancı', icon: Store, desc: 'Ürünlerinizi yönetin' },
  { id: 'admin', label: 'Yönetici', icon: ShieldCheck, desc: 'Sistem yönetimi' },
];

const TRUST_ITEMS = [
  { icon: BadgeCheck, text: 'Doğrulanmış toptancı ağı' },
  { icon: Truck, text: 'Hızlı ve şeffaf sipariş süreci' },
  { icon: Handshake, text: 'Kurumsal destek altyapısı' },
];

type ForgotStep = 'login' | 'request' | 'verify' | 'reset';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoading, error, clearError, setSelectedRole, selectedRole } = useAuthStore();

  const [step, setStep] = useState<ForgotStep>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>(selectedRole ?? 'restaurant');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const ssoError = searchParams.get('sso_error');

  useEffect(() => {
    if (ssoError) {
      setLocalError('Lezzet Yolda üzerinden otomatik giriş tamamlanamadı. Lütfen tekrar deneyin.');
    }
  }, [ssoError]);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const redirectAfterLogin = (userRole: UserRole) => {
    const redirect = searchParams.get('redirect');
    if (redirect) {
      navigate(redirect);
      return;
    }
    if (userRole === 'admin') navigate('/admin');
    else if (userRole === 'wholesaler') navigate('/wholesaler');
    else navigate('/restaurant');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError(null);
    try {
      setSelectedRole(role);
      await login(username, password, role);
      unlockMessageSound();
      if (role === 'wholesaler' || role === 'restaurant') {
        void ensureNotificationPermission();
      }
      redirectAfterLogin(role);
    } catch {
      // error in store
    }
  };

  const handleForgotRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSubmitting(true);
    try {
      await authService.forgotPassword(username);
      setSuccessMsg('Doğrulama kodu WhatsApp ile gönderildi.');
      setStep('verify');
    } catch (err) {
      setLocalError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSubmitting(true);
    try {
      const result = await authService.verifyResetCode(username, resetCode);
      if (!result.valid) {
        setLocalError('Geçersiz veya süresi dolmuş kod.');
        return;
      }
      setStep('reset');
    } catch (err) {
      setLocalError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSubmitting(true);
    try {
      await authService.resetPassword(username, resetCode, newPassword);
      setSuccessMsg('Şifreniz güncellendi. Giriş yapabilirsiniz.');
      setStep('login');
      setPassword('');
      setResetCode('');
      setNewPassword('');
    } catch (err) {
      setLocalError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="section-container py-8 md:py-12">
      <div className="grid min-h-[70vh] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-card md:grid-cols-2">
        <div className="hidden flex-col justify-between bg-slate-900 p-10 md:flex lg:p-12">
          <div>
            <Link to="/" className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-xs font-bold text-slate-900">
                LY
              </span>
              <span className="font-bold text-white">Lezzet Yolda Toptan</span>
            </Link>
            <h1 className="mt-10 text-3xl font-bold leading-tight text-white lg:text-4xl">
              Profesyonel B2B gıda tedarik platformu
            </h1>
            <p className="mt-4 text-base leading-relaxed text-slate-400">
              Restoranlar ve toptancılar için güvenilir, şeffaf ve hızlı dijital tedarik çözümü.
            </p>
          </div>
          <ul className="space-y-4">
            {TRUST_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.text} className="flex items-center gap-3 text-sm text-slate-300">
                  <Icon className="h-5 w-5 shrink-0 text-brand-400" strokeWidth={1.75} />
                  {item.text}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex flex-col justify-center p-6 md:p-10 lg:p-12">
          <div className="mb-8 md:hidden">
            <Link to="/" className="text-sm font-medium text-brand-600 hover:text-brand-700">
              ← Ana sayfa
            </Link>
          </div>

          {step === 'login' && (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Giriş Yap</h2>
                <p className="mt-1 text-sm text-slate-600">Hesabınıza erişmek için bilgilerinizi girin.</p>
              </div>

              <div className="mb-6 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Hesap Türü</p>
                {ROLES.map((r) => {
                  const Icon = r.icon;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setRole(r.id)}
                      className={clsx(
                        'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition',
                        role === r.id
                          ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-500/20'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50',
                      )}
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100">
                        <Icon className="h-5 w-5 text-slate-700" strokeWidth={1.75} />
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">{r.label}</span>
                        <span className="block text-xs text-slate-500">{r.desc}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <Card elevated={false} padding="lg" className="border-slate-200 shadow-none">
                <form onSubmit={handleLogin} className="space-y-4">
                  <Input
                    label="Kullanıcı Adı"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoComplete="username"
                  />
                  <Input
                    label="Şifre"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  {(error || localError) && (
                    <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error ?? localError}</p>
                  )}
                  {successMsg && (
                    <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">{successMsg}</p>
                  )}
                  <Button type="submit" fullWidth loading={isLoading}>
                    Giriş Yap
                  </Button>
                  <button
                    type="button"
                    className="w-full text-sm font-medium text-brand-600 hover:text-brand-700"
                    onClick={() => {
                      setStep('request');
                      setSuccessMsg(null);
                      setLocalError(null);
                    }}
                  >
                    Şifremi Unuttum
                  </button>
                </form>
              </Card>
            </>
          )}

          {step === 'request' && (
            <Card elevated={false} padding="lg" className="border-slate-200 shadow-none">
              <h2 className="mb-1 text-xl font-bold text-slate-900">Şifre Sıfırlama — Adım 1/3</h2>
              <p className="mb-6 text-sm text-slate-600">
                Kullanıcı adınızı girin. Doğrulama kodu WhatsApp ile gönderilecektir.
              </p>
              <form onSubmit={handleForgotRequest} className="space-y-4">
                <Input label="Kullanıcı Adı" value={username} onChange={(e) => setUsername(e.target.value)} required />
                {localError && <p className="text-sm text-red-600">{localError}</p>}
                <Button type="submit" fullWidth loading={submitting}>
                  Kod Gönder
                </Button>
                <Button type="button" variant="ghost" fullWidth onClick={() => setStep('login')}>
                  Geri Dön
                </Button>
              </form>
            </Card>
          )}

          {step === 'verify' && (
            <Card elevated={false} padding="lg" className="border-slate-200 shadow-none">
              <h2 className="mb-1 text-xl font-bold text-slate-900">Şifre Sıfırlama — Adım 2/3</h2>
              <p className="mb-6 text-sm text-slate-600">WhatsApp ile gelen 6 haneli kodu girin.</p>
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <Input
                  label="Doğrulama Kodu"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  required
                  maxLength={6}
                />
                {localError && <p className="text-sm text-red-600">{localError}</p>}
                <Button type="submit" fullWidth loading={submitting}>
                  Doğrula
                </Button>
                <Button type="button" variant="ghost" fullWidth onClick={() => setStep('request')}>
                  Geri Dön
                </Button>
              </form>
            </Card>
          )}

          {step === 'reset' && (
            <Card elevated={false} padding="lg" className="border-slate-200 shadow-none">
              <h2 className="mb-1 text-xl font-bold text-slate-900">Şifre Sıfırlama — Adım 3/3</h2>
              <p className="mb-6 text-sm text-slate-600">Yeni şifrenizi belirleyin.</p>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <Input
                  label="Yeni Şifre"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                />
                {localError && <p className="text-sm text-red-600">{localError}</p>}
                <Button type="submit" fullWidth loading={submitting}>
                  Şifreyi Güncelle
                </Button>
              </form>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
